'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { trackEvent } from '@/lib/analytics/posthog'
import { recordMemberActivity } from '@/lib/analytics/memberActivity'

type Props = {
  locale: string
  heading?: string
  placeholder?: string
  suggestions?: string[]
  className?: string
  // Where this instance is mounted. Sent with the analytics event so the same
  // component on /home and /dashboard can be filtered apart in PostHog.
  surface?: 'home' | 'dashboard'
}

const DEFAULT_SUGGESTIONS = [
  'battery recycling process steps',
  'thermal runaway',
  'cell manufacturing',
  'safety training',
]

export function HomeSearchHero({
  locale,
  heading = 'What brings you here today?',
  placeholder = 'Try "battery recycling process steps" or "thermal runaway"…',
  suggestions = DEFAULT_SUGGESTIONS,
  className = '',
  surface = 'home',
}: Props) {
  const router = useRouter()
  const [value, setValue] = useState('')

  const goToSearch = (query: string, source: 'input' | 'suggestion') => {
    const trimmed = query.trim()
    if (!trimmed) return
    trackEvent('home_search_submitted', {
      query: trimmed,
      source,
      locale,
      surface,
    })
    // Signed-in members only (EA's own record, member_activity), with this page as the path;
    // the search page then skips its repeat of the same query.
    void recordMemberActivity('search', { searchText: trimmed })
    router.push(`/${locale}/search?q=${encodeURIComponent(trimmed)}`)
  }

  return (
    <section className={`mx-auto w-full max-w-[57.6rem] px-6 ${className}`}>
      {heading && (
        <h2 className="mb-10 text-center font-body text-4xl font-bold lg:text-5xl">
          {heading}
        </h2>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          goToSearch(value, 'input')
        }}
        className="flex w-full items-center gap-3 rounded-sm bg-white px-5 py-4 shadow-md ring-1 ring-black/5"
      >
        <input
          type="search"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className="min-w-0 flex-1 bg-transparent px-1 py-2 text-base sm:px-3 sm:text-xl text-gray-900 placeholder:text-gray-500 focus:outline-none"
        />
        <button
          type="submit"
          className="shrink-0 rounded-sm bg-purple-200 px-4 sm:px-8 py-3.5 text-base sm:text-lg font-medium text-purple-900 transition-colors hover:bg-purple-300 disabled:opacity-50"
          disabled={!value.trim()}
        >
          Search
        </button>
      </form>

      {suggestions.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <span className="text-base text-gray-600">Try:</span>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setValue(s)
                goToSearch(s, 'suggestion')
              }}
              className="whitespace-nowrap rounded-sm bg-white px-5 py-2 text-base text-gray-800 shadow-sm ring-1 ring-black/5 transition-colors hover:bg-gray-50"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </section>
  )
}
